import { useState, useMemo } from 'react';
import * as yaml from 'js-yaml';
import { Card, CardContent } from '@/components/ui/card';
import { ApiDocsLayout } from '@/components/api-docs/ApiDocsLayout';
import type { OpenAPISpec, EndpointInfo, PathItem } from '@/types/openapi';
import type { AudienceFilter } from '@/components/api-docs/ApiDocsLayout';

// Single source of truth for the API docs (audience-tagged via x-audience)
import openapiYamlRaw from '@/data/openapi.yaml?raw';

const AUDIENCE_STORAGE_KEY = 'apiDocsAudience';

function readStoredAudience(): AudienceFilter {
  try {
    return localStorage.getItem(AUDIENCE_STORAGE_KEY) === 'all' ? 'all' : 'saas';
  } catch {
    return 'saas';
  }
}

interface SpecResult {
  spec: OpenAPISpec | null;
  error: string | null;
}

// The spec is a static import, so it is parsed once when the page mounts (no effect needed).
function parseSpec(): SpecResult {
  try {
    // Parse YAML string to JavaScript object using js-yaml
    return { spec: yaml.load(openapiYamlRaw) as OpenAPISpec, error: null };
  } catch (err) {
    console.error('Failed to load API spec:', err);
    return { spec: null, error: err instanceof Error ? err.message : 'Failed to parse API spec' };
  }
}

export function ApiDocsPage() {
  const [{ spec, error }] = useState<SpecResult>(parseSpec);
  const [audience, setAudience] = useState<AudienceFilter>(readStoredAudience);

  const handleAudienceChange = (next: AudienceFilter) => {
    setAudience(next);
    try {
      localStorage.setItem(AUDIENCE_STORAGE_KEY, next);
    } catch {
      // Storage unavailable (private mode); keep the in-memory choice.
    }
  };
  const endpointsByTag = useMemo(() => {
    if (!spec) return {};

    const grouped: Record<string, EndpointInfo[]> = {};
    const methods = ['get', 'post', 'put', 'delete', 'patch'] as const;

    for (const [path, pathItem] of Object.entries(spec.paths)) {
      for (const method of methods) {
        const operation = (pathItem as PathItem)[method];
        // Untagged operations count as SaaS-facing; admin ones only show under "All".
        if (operation && (audience === 'all' || operation['x-audience'] !== 'admin')) {
          const tags = operation.tags || ['Default'];
          for (const tag of tags) {
            if (!grouped[tag]) {
              grouped[tag] = [];
            }
            grouped[tag].push({ path, method, operation });
          }
        }
      }
    }

    // Sort endpoints within each tag by path
    for (const tag of Object.keys(grouped)) {
      grouped[tag].sort((a, b) => {
        const methodOrder: Record<string, number> = {
          get: 0,
          post: 1,
          put: 2,
          patch: 3,
          delete: 4,
        };
        const methodDiff =
          (methodOrder[a.method] || 5) - (methodOrder[b.method] || 5);
        if (methodDiff !== 0) return methodDiff;
        return a.path.localeCompare(b.path);
      });
    }

    return grouped;
  }, [spec, audience]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="border-destructive">
          <CardContent className="p-8">
            <p className="text-destructive">Error: {error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ApiDocsLayout
      spec={spec!}
      endpointsByTag={endpointsByTag}
      audience={audience}
      onAudienceChange={handleAudienceChange}
    />
  );
}